import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import Auth from "../../models/Auth";
import { LOCAL_URL } from "../../helper/constant";
import qs from "qs";

export const cpadminSlice = createApi({
    reducerPath: "cpadminService",
    baseQuery: fetchBaseQuery({
        baseUrl: `${LOCAL_URL}`,
        prepareHeaders: (headers) => {
            headers.set("Authorization", "Bearer " + Auth.token);
            headers.set("x-portal", Auth.portalId);
            headers.set("x-userId", Auth.userId);
            return headers;
        },
    }),
    tagTypes: ["PortalUserGroup"],
    endpoints: (builder) => ({
        getInstanceId: builder.query({
            query: (requestId) => ({
                url: "/requestDetails/getRequestInstanceId",
                method: "POST",
                body: {
                    requestId,
                },
            }),
            transformResponse: (responseData) => responseData.data,
        }),
        // actually can call it before we have data in Auth object
        getPortal: builder.query({
            query: (payload) => {
                let id;
                if (typeof payload !== "object" || !id) {
                    id = Auth.portalId;
                } else {
                    id = payload.id;
                }
                return {
                    url: `/portal/getPortal/${id}`,
                    method: "GET",
                };
            },
            transformResponse: (responseData) => responseData.data,
        }),
        getRequestDetail: builder.query({
            query: ({ id }) => ({
                url: "/requestDetails/getRequestDetails",
                method: "POST",
                body: { requestId: id },
            }),
            transformResponse: (responseData) => responseData.data,
        }),
        getRecyclingPartners: builder.query({
            query: ({ clientId, portalId, countryIds }) => ({
                url: "/recyclingpartnerportal/fetchPortalProcessingPartner",
                method: "POST",
                body: { clientId, portalId, countryId: countryIds },
            }),
            transformResponse: (responseData) => responseData.data,
        }),
        getProgramCountries: builder.query({
            query: ({ portalId }) => ({
                url: "/programCountries/listProgramCountries",
                method: "POST",
                body: { portalId },
            }),
            transformResponse: (responseData) => responseData.data,
        }),
        getCountries: builder.query({
            query: () => ({
                url: "/countries/getcountrieslist",
                method: "GET",
            }),
            transformResponse: (responseData) => responseData.data,
        }),
        getPortalUserCountries: builder.query({
            query: () => {
                const queryStr = qs.stringify({
                    portalId: Auth.portalId,
                    userId: Auth.userId,
                });
                return {
                    url: `/portalusercountries/getPortalUserCountries?${queryStr}`,
                    method: "GET",
                };
            },
            transformResponse: (responseData) => responseData.data,
        }),
        getCarrierContacts: builder.query({
            query: ({ carrierId }) => ({
                url: "/carrierContacts/getCarrierContact",
                method: "POST",
                payload: { carrierId },
            }),
            transformResponse: (responseData) => responseData.data,
        }),
        getPortalGroups: builder.query({
            query: ({ portalId, clientId }) => {
                if (!portalId && !clientId) {
                    portalId = Auth.portalId;
                    clientId = Auth.clientId;
                }
                return {
                    url: `/portalgroups/getGroups?portalId=${portalId}&clientId=${clientId}`,
                    method: "GET",
                };
            },
        }),
        getPortalUserGroup: builder.query({
            query: ({ userId, portalId, clientId }) => {
                if (!userId) userId = Auth.userId;
                if (!portalId && !clientId) {
                    portalId = Auth.portalId;
                    clientId = Auth.clientId;
                }
                return {
                    url: `/portalusergroups/getbyuid?userId=${userId}&portalId=${portalId}&clientId=${clientId}`,
                    method: "GET",
                };
            },
            transformResponse: (responseData) => responseData.data[0],
            providesTags: (result, error, arg) => {
                return [{ type: "PortalUserGroup", id: arg.userId }];
            },
        }),
        updatePortalUserGroup: builder.mutation({
            query: ({ userId, groupInfo, portalId, clientId }) => ({
                url: "/portalusergroups/setGroup",
                method: "POST",
                body: { userId, groupInfo, portalId, clientId },
            }),
            invalidatesTags: (result, error, arg) => [{ type: "PortalUserGroup", id: arg.userId }],
        }),
        updateRequestDetail: builder.mutation({
            query: ({ id, ...rest }) => ({
                url: "/requestDetails/updateRequestDetails",
                method: "POST",
                body: { id, ...rest },
            }),
            async onQueryStarted({ id, ...rest }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    cpadminSlice.util.updateQueryData("getRequestDetail", { id }, (draft) => {
                        Object.assign(draft, rest);
                    })
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
        getSavedFilterDataByUserId: builder.query({
            query: ({ pageName }) => ({
                url: `/searchfilters/getSavedFilterDataByUserId?userId=${Auth.userId}&pageName=${pageName}`,
                method: "GET",
            }),
            transformResponse: (responseData) => responseData.data,
        }),
        saveFilter: builder.mutation({
            query: (payload) => ({
                url: `/searchfilters/saveFilter`,
                method: "POST",
                body: payload,
            }),
            transformResponse: (responseData) => responseData.data,
        }),
        deleteFilter: builder.mutation({
            query: ({ id }) => ({
                url: `/searchfilters/deleteFilter`,
                method: "POST",
                body: { id },
            }),
            transformResponse: (responseData) => responseData.data,
        }),
    }),
});

export const {
    useGetPortalUserCountriesQuery,
    useGetPortalQuery,
    useGetCountriesQuery,

    useGetInstanceIdQuery,
    useGetRequestDetailQuery,
    useLazyGetRequestDetailQuery,
    useGetRecyclingPartnersQuery,
    useLazyGetRecyclingPartnersQuery,
    useGetProgramCountriesQuery,
    useLazyGetProgramCountriesQuery,
    useGetPortalGroupsQuery,
    useGetPortalUserGroupQuery,
    usePrefetch,
    useUpdatePortalUserGroupMutation,
    useGetSavedFilterDataByUserIdQuery,
    useSaveFilterMutation,
    useDeleteFilterMutation
} = cpadminSlice;
